import type { LstWithdrawalRequest } from '../types'
import type { LstWithdrawalReader } from './shared'

/**
 * Lair Finance stKAIA — log-free via caller-supplied IDs.
 *
 * Unstaking is a two-step 7-day queue on the stKAIA contract:
 * `unstake(node, amount)` burns stKAIA and emits a request, then
 * `claim(requestId)` pays out after the unbonding period. The contract
 * exposes no confirmed public getter to enumerate a user's pending
 * requests (only `claim(uint256)`), so — per the no-logs constraint —
 * the reader expects the caller (frontend / worker-api) to have
 * captured each `requestId` from the unstake tx receipt and to pass the
 * accumulated list as `knownIds`.
 *
 * Without a detail getter we cannot resolve the settled amount or the
 * exact `readyAt`, so each known id is surfaced as a bare `pending`
 * request. If a public request-detail getter is later confirmed on the
 * stKAIA contract, enrich here: resolve `amountUnderlying` and
 * `readyAt = requestTs + 7d`, then derive `status` via `computeStatus`.
 *
 * Returns `[]` when no IDs are supplied — no enumeration is attempted.
 */
export const readerLair: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry, knownIds) => {
    if (!knownIds || knownIds.length === 0) return []

    // Lair uses plain bigint request IDs — drop any composite ids.
    const ids = knownIds.filter((v): v is bigint => typeof v === 'bigint')
    if (ids.length === 0) return []

    return ids.map((id) => ({
      lst: entry.lst,
      brand: entry.brand,
      symbol: entry.symbol,
      requestId: id.toString(),
      // Amount/readyAt are unknown without a detail getter; UI should
      // label this as "pending unstake" and rely on the 7-day cooldown.
      amountUnderlying: '0',
      status: 'pending' as const,
    }))
  },
}
